"use client";

import { m, useInView } from "framer-motion";
import React, { useRef, ComponentPropsWithRef, useCallback, useSyncExternalStore } from "react";
import TransitionLink from "../transitionLink";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import Card from "../card";
import { ContentButton } from "../button";
import SectionHeader from "../sectionHeader";

interface CardItem {
  icon?: { asset: { url: string }; alt?: string };
  letter?: string;
  title: string;
  text: string;
  link?: { href: string; label: string };
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    text?: string;
    cards?: CardItem[];
    cardVariant?: "frame" | "filled";
    columns?: number;
    button?: { title: string; page: { slug: { current: string } } };
    isCarousel?: boolean;
    darkerBackground?: boolean;
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

export default function CardsSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
  });
  const hasThreeColumns = data?.columns === 3 || data?.cards?.length === 3;

  const content = (
    <m.div
      className={`${data?.isCarousel ? "" : "container mx-auto"} flex flex-col items-center gap-12`}
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
      ref={ref}
    >
      {data?.isCarousel ? (
        <div className="container mx-auto">
          <SectionHeader title={data?.title} headline={data?.headline} text={data?.text} />
        </div>
      ) : (
        <SectionHeader title={data?.title} headline={data?.headline} text={data?.text} />
      )}
      {data?.isCarousel ? (
        <CardsCarousel data={data} />
      ) : (
        <div
          className={`grid auto-rows-fr sm:grid-cols-2 ${hasThreeColumns ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-4 w-full`}
        >
          {data?.cards?.map((item, index) => (
            <Card
              key={index}
              icon={item.icon}
              letter={item.letter}
              title={item.title}
              text={item.text}
              variant={data?.cardVariant}
              link={item.link}
              fillColumn={hasThreeColumns}
            />
          ))}
        </div>
      )}

      {!data?.isCarousel && data?.button && (
        <m.div
          variants={itemVariants}
          className="flex justify-start items-start"
        >
          <TransitionLink
            classes="btn btn-arrow group relative overflow-hidden"
            href={`/${data.button.page.slug.current}`}
          >
            <ContentButton>{data.button.title}</ContentButton>
          </TransitionLink>
        </m.div>
      )}
    </m.div>
  );

  return (
    <section className={`section ${data?.darkerBackground ? "bg-primary" : ""}`}>
      {content}
    </section>
  );
}

type PropType = {
  options?: EmblaOptionsType;
  data?: Props["data"];
};

const CardsCarousel: React.FC<PropType> = (props) => {
  const { data } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({ containScroll: false });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const cards = data?.cards ?? [];

  return (
    <div className="w-full">
      {/* Mobile: Embla carousel with dots */}
      <div className="md:hidden">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex touch-pan-y gap-2">
            {cards.map((item, index) => (
              <m.div
                variants={itemVariants}
                className="min-w-[0px] flex-[0_0_80%] sm:flex-[0_0_40%]"
                key={index}
                style={{ transform: "translate3d(0, 0, 0)" }}
              >
                <Card
                  icon={item.icon}
                  letter={item.letter}
                  title={item.title}
                  text={item.text}
                  compact
                />
              </m.div>
            ))}
          </div>
        </div>

        <m.div
          variants={itemVariants}
          className="embla__dots flex gap-2 w-full justify-center items-center mt-4"
        >
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={"frame mono w-10 h-10 transition-all".concat(
                index === selectedIndex ? " opacity-100" : " opacity-30 scale-90"
              )}
            >
              {index + 1}
            </DotButton>
          ))}
        </m.div>
      </div>

      {/* Desktop/Tablet: slow auto-scrolling marquee */}
      <div className="hidden md:block overflow-hidden">
        <m.div
          variants={itemVariants}
          className="flex gap-4 marquee-scroll"
          style={{ width: "max-content" }}
        >
          {[...cards, ...cards, ...cards, ...cards].map((item, index) => (
            <div key={index} className="w-[280px] shrink-0">
              <Card
                icon={item.icon}
                letter={item.letter}
                title={item.title}
                text={item.text}
                compact
              />
            </div>
          ))}
        </m.div>
      </div>
    </div>
  );
};

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

const emptyScrollSnaps: number[] = [];

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const subscribe = useCallback((onChange: () => void) => {
    if (!emblaApi) return () => {};
    emblaApi.on("reInit", onChange).on("select", onChange);
    return () => {
      emblaApi.off("reInit", onChange).off("select", onChange);
    };
  }, [emblaApi]);

  const selectedIndex = useSyncExternalStore(
    subscribe,
    () => emblaApi?.selectedScrollSnap() ?? 0,
    () => 0,
  );
  const scrollSnaps = useSyncExternalStore(
    subscribe,
    () => emblaApi?.scrollSnapList() ?? emptyScrollSnaps,
    () => emptyScrollSnaps,
  );

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export const DotButton: React.FC<ComponentPropsWithRef<"button">> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button type="button" {...restProps}>
      {children}
    </button>
  );
};
